import { IonCard, IonCardHeader, IonCardContent, IonButton, IonText, IonChip, IonLabel } from '@ionic/react';
// src/components/DebugOverlay.tsx
import React, { useState, useEffect, useRef } from 'react';

import Logger, { LogEntry } from '@/utils/CapcitorLog';
import { isDevelopment } from '@/utils/environment';

interface DebugOverlayProps {
  initiallyVisible?: boolean;
}

export const DebugOverlay: React.FC<DebugOverlayProps> = ({ initiallyVisible = true }) => {
  // Hide component if the app is not in development mode
  if (!isDevelopment) {
    return null;
  }
  const [visible, setVisible] = useState(initiallyVisible);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const logsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsubscribe = Logger.subscribe((entry) => {
      setLogs((prevLogs) => [...prevLogs, entry].slice(-100));
    });

    setLogs(Logger.getLogs());
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (visible && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, visible]);

  if (!visible) {
    return (
      <IonButton
        onClick={() => setVisible(true)}
        size='small'
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '300px',
          zIndex: 999,
        }}>
        Show Logs
      </IonButton>
    );
  }

  const getLogColor = (level: LogEntry['level']): string => {
    switch (level) {
      case 'debug':
        return 'medium';
      case 'info':
        return 'primary';
      case 'warn':
        return 'warning';
      case 'error':
        return 'danger';
      default:
        return 'medium';
    }
  };

  return (
    <IonCard
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '300px',
        width: '90%',
        maxWidth: '400px',
        maxHeight: '300px',
        margin: 0,
        zIndex: 999,
        backgroundColor: 'var(--ion-background-color)',
      }}>
      <IonCardHeader
        style={{
          padding: '10px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        <IonText>Debug Logs</IonText>
        <div>
          <IonButton color='danger' onClick={() => Logger.clear()} size='small' style={{ marginRight: '8px' }}>
            Clear
          </IonButton>
          <IonButton onClick={() => setVisible(false)} size='small'>
            Hide
          </IonButton>
        </div>
      </IonCardHeader>

      <IonCardContent
        style={{
          maxHeight: '250px',
          overflowY: 'auto',
          padding: '10px',
          fontSize: '12px',
        }}>
        {logs.map((log, index) => (
          <div key={index} style={{ marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <IonText style={{ color: 'var(--ion-color-medium)' }}>
                {new Date(log.timestamp).toLocaleTimeString()}
              </IonText>
              <IonChip color={getLogColor(log.level)} style={{ margin: 0, height: '20px' }}>
                <IonLabel>{log.level.toUpperCase()}</IonLabel>
              </IonChip>
              <IonText>{log.message}</IonText>
            </div>
            {log.details && (
              <pre
                style={{
                  marginLeft: '20px',
                  marginTop: '4px',
                  color: 'var(--ion-color-medium)',
                  fontSize: '11px',
                  whiteSpace: 'pre-wrap',
                }}>
                {typeof log.details === 'string' ? log.details : JSON.stringify(log.details, null, 2)}
              </pre>
            )}
          </div>
        ))}
        <div ref={logsEndRef} />
      </IonCardContent>
    </IonCard>
  );
};
